import { Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { DeviceDetectorService } from 'ngx-device-detector';
import { distinct, filter } from 'rxjs';
import { NotificationIconsType, NotificationNavigateType, NotificationType } from 'src/app/_models/notification.enum';
import { NotificationService } from 'src/app/_services/notification.service';
import { TenantInnerService } from 'src/app/_services/tenant-inner.service';
import { UploadFileService } from 'src/app/upload/_services/upload-file.service';
import { SignalRService } from '../../_services/signalR.service';
import { TokenService } from '../../_services/token.service';
import { getMentionHeader } from 'src/app/shared/_helpers/notification.helper';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NotificationsComponent {
  @ViewChild('pop') pop!: PopoverDirective;
  public notificationType = NotificationType;
  public notificationIconsType = NotificationIconsType;
  isLoading = true;
  totalUnRead = 0;
  totalCount = 0;
  notifications: any[] = [];
  params = {
    skip: 0,
    size: 10,
    showOnlyUnread: false,
  };
  isAdmin: boolean = false;
  isMobile = false;

  constructor(
    public router: Router,
    public uploadFileService: UploadFileService,
    private notificationService: NotificationService,
    private signalRService: SignalRService,
    private tenantInnerService: TenantInnerService,
    private tokenStorageService: TokenService,
    private deviceService: DeviceDetectorService
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinct((_x) => this.router.url.includes('/admin'))
      )
      .subscribe((event) => this.getNotifications());

    this.notificationService.getNotifications(0, 0, true, true).subscribe((res) => {
      this.notificationService.adminNotifications.next(res.totalUnreadCount);
    });
    this.signalRService.notification.subscribe((data) => {
      if (data !== null) {
        const raw = JSON.parse(data);
        const notify = this.toLowerCamelCase(raw);
        this.notifications.unshift(notify);
        if (raw.IsAdminNotification) {
          this.notificationService.adminNotifications.next(+0.5);
        }
        if ((this.isAdmin && raw.IsAdminNotification) || (!this.isAdmin && !raw.IsAdminNotification)) {
          this.totalUnRead = this.totalUnRead + 1;
        }
      }
    });
    this.isMobile = this.deviceService.isMobile();
  }

  openSettings(event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      return;
    }
    event?.preventDefault();
    this.pop?.hide();
    this.router.navigate(['./settings/notifications']);
  }

  goToUpload(event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      return;
    }
    event?.preventDefault();
    this.pop?.hide();
    this.router.navigate(['./wastewater/upload-content/upload']);
  }

  getNotificationUrl(notification: any): string {
    if (notification.linkToUrl) {
      if (notification.type === NotificationType.Mention) {
        return notification.linkToUrl;
      } else if (notification.type === NotificationType.Ticket) {
        return this.isAdmin
          ? '/admin/errors&ambiguities/ticket/' + notification.linkToUrl
          : NotificationNavigateType[notification.type] + notification.linkToUrl;
      } else if (notification.type === NotificationType.Datenupload) {
        const slashIdx = (notification.linkToUrl as string).indexOf('/');
        if (slashIdx === -1) return NotificationNavigateType[0];
        const uploadId = notification.linkToUrl.substring(0, slashIdx);
        const baseRoute = this.isAdmin ? './admin/upload-content/detail/' : './wastewater/upload-content/detail/';
        return baseRoute + uploadId;
      } else if (
        notification.type === NotificationType['Video Konvertierung erfolgreich'] ||
        notification.type === NotificationType['Video Konvertierung fehlgeschlagen']
      ) {
        return NotificationNavigateType[8] + notification.linkToUrl;
      } else {
        const [, , ticketId] = notification.linkToUrl.split(' - ');
        const baseRoute = NotificationNavigateType[notification.type];
        return notification.type === NotificationType.InspectionTicketFlow && ticketId
          ? `${baseRoute}${ticketId}`
          : baseRoute;
      }
    }
    return NotificationNavigateType[notification.type] ?? '#';
  }

  toggleSettingsNotifications(): void {
    this.params.showOnlyUnread = !this.params.showOnlyUnread;
    this.getNotifications();
  }

  getNotifications(isNextPage = false): void {
    this.isAdmin = this.router.url.includes('/admin');
    this.isLoading = true;
    this.params.skip = isNextPage ? this.params.skip + this.params.size : 0;
    this.notificationService
      .getNotifications(this.params.skip, this.params.size, this.params.showOnlyUnread, this.isAdmin)
      .subscribe((res) => {
        this.totalUnRead = res.totalUnreadCount;
        this.totalCount = res.totalCount;
        if (isNextPage) {
          this.notifications = [...this.notifications, ...res.notifications];
        } else {
          this.notifications = res.notifications;
        }
        this.isLoading = false;
      });
  }

  readNotifications(id: string, all: boolean, e?: MouseEvent): void {
    this.notificationService.readNotifications(id, all).subscribe();
    if (all) {
      this.notifications.forEach((not) => (not.isRead = true));
    }
    if (id) {
      this.notifications.forEach((not) => {
        if (id === not.id) {
          not.isRead = true;
        }
      });
    }
    e?.stopPropagation();
  }

  deleteNotifications(id: string, all: boolean, e?: MouseEvent): void {
    this.notificationService.deleteNotifications(id, all).subscribe(() => this.getNotifications());
    e?.stopPropagation();
    this.notificationService.adminNotifications.next(0);
  }

  navigate(notification: any, event?: MouseEvent): void {
    if (event?.ctrlKey || event?.metaKey) {
      this.readNotifications(notification.id, false);
      return;
    }
    event?.preventDefault();
    this.readNotifications(notification.id, false);
    this.pop?.hide();
    if (notification.linkToUrl !== null && notification.linkToUrl !== '') {
      if (notification.type === NotificationType.Mention) {
        const sourceAndTenant = this.getMentionSourceAndTenant(notification.text);
        if (sourceAndTenant && this.isCurrentTenant(sourceAndTenant)) {
          const [tenant, source] = sourceAndTenant.split(' - ');
          this.tenantInnerService.switchTenantByName(tenant, source, notification.linkToUrl);
        } else {
          this.router.navigateByUrl(notification.linkToUrl);
        }
      } else if (notification.type === NotificationType.Ticket) {
        this.router.navigate(
          this.isAdmin
            ? ['./admin/errors&ambiguities/ticket/' + notification.linkToUrl]
            : [NotificationNavigateType[notification.type] + notification.linkToUrl]
        );
      } else if (notification.type === NotificationType.Datenupload) {
        const slashIdx = (notification.linkToUrl as string).indexOf('/');
        if (slashIdx === -1) {
          this.router.navigate([NotificationNavigateType[0]]);
          return;
        }
        const uploadId = notification.linkToUrl.substring(0, slashIdx);
        const sourceAndTenant = notification.linkToUrl.substring(slashIdx + 1);
        const [notificationSource, notificationTenant] = sourceAndTenant.split(' - ');
        const activeTenant = this.tokenStorageService.GetTenant();
        const activeSource = this.tokenStorageService.GetSource();
        const baseRoute = this.isAdmin ? './admin/upload-content/detail/' : './wastewater/upload-content/detail/';
        const targetUrl = `${baseRoute}${uploadId}`;
        if (notificationTenant !== activeTenant || notificationSource !== activeSource) {
          this.tenantInnerService.switchTenantByName(notificationTenant, notificationSource, targetUrl);
        } else {
          this.router.navigate([targetUrl]);
        }
      } else if (
        notification.type === NotificationType['Video Konvertierung erfolgreich'] ||
        notification.type === NotificationType['Video Konvertierung fehlgeschlagen']
      ) {
        this.router.navigate([NotificationNavigateType[8] + notification.linkToUrl]);
      } else {
        const [notificationSource, notificationTenant, ticketId] = notification.linkToUrl.split(' - ');
        const activeTenant = this.tokenStorageService.GetTenant();
        const activeSource = this.tokenStorageService.GetSource();

        const isInspectionMention = notification.type === NotificationType.InspectionTicketFlow;
        const baseRoute = NotificationNavigateType[notification.type];
        const targetUrl = isInspectionMention && ticketId
          ? `${baseRoute}${ticketId}`
          : baseRoute;

        const needsTenantSwitch = notificationTenant !== activeTenant || notificationSource !== activeSource;

        if (needsTenantSwitch) {
          this.tenantInnerService.switchTenantByName(notificationTenant, notificationSource, targetUrl);
        } else {
          this.router.navigate([targetUrl]);
        }
      }
    } else {
      this.router.navigate([NotificationNavigateType[notification.type]]);
    }
  }

  onScroll(event: any): void {
    if (
      event.target.offsetHeight + event.target.scrollTop >= event.target.scrollHeight &&
      this.totalCount > this.notifications.length
    ) {
      this.getNotifications(true);
    }
  }

  getMentionSourceAndTenant(text: string): string | undefined {
    const parts = this.getMentionParts(text);
    return parts.length > 1 ? parts[parts.length - 1] : undefined;
  }

  getMentionTenantName(text: string): string {
    const tenantName = this.getMentionSourceAndTenant(text)?.split(' - ')[0] ?? '';
    return tenantName ? this.tenantInnerService.getTenantDisplayName(tenantName) : '';
  }

  getNotificationTenantName(not: any): string {
    if (not.type === NotificationType.Mention) {
      return this.getMentionTenantName(not.text);
    }
    if (not.type === NotificationType.InspectionTicketFlow) {
      // linkToUrl for this type is "Source - Tenant - TicketId", not a URL path.
      const [source, tenant] = (not.linkToUrl ?? '').split(' - ');
      if (!tenant || !source) {
        return '';
      }
      const isCurrent = tenant === this.tokenStorageService.GetTenant() && source === this.tokenStorageService.GetSource();
      return isCurrent ? '' : this.tenantInnerService.getTenantDisplayName(tenant);
    }
    return '';
  }

  getMentionParts(text: string): string[] {
    return (text ?? '').split('\n');
  }

  getMentionContent(text: string): string {
    const parts = this.getMentionParts(text);
    // First line is the header, last line is the tenant/source - anything in between is the actual comment content.
    return parts.length > 2 ? parts.slice(1, parts.length - 1).join('\n') : '';
  }

  getMentionName(text: string): string {
    const line = this.getMentionParts(text)[0];
    const idx = line.indexOf(' hat');
    return idx !== -1 ? line.substring(0, idx) : line;
  }

  getMentionSuffix(text: string): string {
    const line = this.getMentionParts(text)[0];
    const idx = line.indexOf(' hat');
    return idx !== -1 ? line.substring(idx) : '';
  }

  getMentionHeader(notification: any): string {
    return getMentionHeader(notification);
  }

  getTicketDetailParts(text: string): string[] {
    const idx = text.indexOf(' Ticket');
    const parts = idx !== -1
      ? [text.substring(0, idx), text.substring(idx + ' Ticket'.length)]
      : [text, ''];
    return parts.map(part =>
      part.replace(/\u201E(.*?)\u201C/g, '<strong>\u201E$1\u201C</strong>')
    );
  }


  isCurrentTenant(link: string) {
    if (!link) {
      return false;
    }
    const sourceAndTenant = link.split(' - ');
    const curTenant = this.tokenStorageService.GetTenant();
    const curServer = this.tokenStorageService.GetSource();
    return sourceAndTenant[0] !== curTenant || sourceAndTenant[1] !== curServer;
  }

  getTenantByLink(link: string) {
    const sourceAndTenant = link.split(' - ');
    return sourceAndTenant[1];
  }

  private toLowerCamelCase(obj: any): any {
    const result: any = {};
    for (const key of Object.keys(obj)) {
      result[key.charAt(0).toLowerCase() + key.slice(1)] = obj[key];
    }
    return result;
  }
}
